import { FunctionComponent } from "preact";
import classNames from "helpers/classNames";
import styles from "./TCFPurposeSettings.module.scss";
import useTranslation from "hooks/useTranslation";
import Spinner from "components/Spinner";
import useTCFDynamicImport from "hooks/useTCFDynamicImport";
import { ITCFGlobalVendorList as ITCFGlobalVendorListResponse } from "types/tcf";
import { IAB_PURPOSES } from "constants/categories";
import useTCFUserConsent from "hooks/useTCFUserConsent";
import {
  TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST,
  TCF_VENDOR_CATEGORIES_TYPES,
  TCFLegIntCategoryName,
} from "helpers/tcf/constantsTCF";
import useAppState from "hooks/useAppState";
import {
  TCFCategoryTypes,
  type ITCFLegIntCategoryName,
  type ITCFVendorCategory,
} from "helpers/tcf/TCFConsentProvider/types";
import { TEXT_MEDIUM } from "styles/bootstrap";
import isTCFCategoryPresent from "helpers/tcf/tcfHelpers/isTCFCategoryPresent";
import getTCFCategoryVendorCount from "helpers/tcf/tcfHelpers/getTCFCategoryVendorCount";

const cx = classNames(styles);

const TCFPurposeSettings: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    tempConsent,
    isCategoryAccepted,
    toggleCategory,
    isCategoryLegitimateInterestAccepted,
    toggleCategoryLegitimateInterest,
  } = useTCFUserConsent();
  const { widgetConfig, gvl, tcfVendorsList } = useAppState();
  const { TCFPurposeItem } = useTCFDynamicImport(widgetConfig);

  const purposes = (gvl: ITCFGlobalVendorListResponse) => {
    return TCF_VENDOR_CATEGORIES_TYPES.map(
      (
        type: {
          key: ITCFVendorCategory;
          legIntName?: ITCFLegIntCategoryName;
          hasSwitch: boolean;
          hasLegitimateInterest: boolean;
        },
        index
      ) => {
        const purposesArray = Object.entries(gvl[type.key]);

        return (
          <div key={`${type.key}-${index}`} class={cx("py-1")}>
            {purposesArray.length > 0 && (
              <>
                <div class={cx("mb-2", TEXT_MEDIUM)}>
                  <h3>{t(`${IAB_PURPOSES}.${type.key}`)}</h3>
                </div>
                {purposesArray.map(([, item]) => {
                  let isCategoryPresent: boolean = false;
                  // Check if it's category 'purposes' because in that case, check also for legitimate interest
                  if (type.key === TCFCategoryTypes.PURPOSES) {
                    isCategoryPresent =
                      isTCFCategoryPresent(
                        type.key,
                        item.id,
                        gvl,
                        tcfVendorsList
                      ) ||
                      isTCFCategoryPresent(
                        TCFLegIntCategoryName,
                        item.id,
                        gvl,
                        tcfVendorsList
                      );
                  } else {
                    isCategoryPresent = isTCFCategoryPresent(
                      type.key,
                      item.id,
                      gvl,
                      tcfVendorsList
                    );
                  }

                  if (!isCategoryPresent) {
                    return null;
                  }

                  return (
                    TCFPurposeItem &&
                    isCategoryPresent && (
                      <TCFPurposeItem
                        description={item.description}
                        illustrations={item.illustrations}
                        title={item.name}
                        numberOfVendors={getTCFCategoryVendorCount(
                          type.key,
                          item.id,
                          gvl,
                          tcfVendorsList
                        )}
                        hasSwitch={type.hasSwitch}
                        hasLegitimateInterest={
                          type.hasLegitimateInterest &&
                          !TCF_PURPOSES_DISABLED_LEGITIMATE_INTEREST.includes(
                            item.id
                          )
                        }
                        isAccepted={isCategoryAccepted(
                          type.key,
                          item.id,
                          tempConsent
                        )}
                        isLegitimateInterestAccepted={isCategoryLegitimateInterestAccepted(
                          type.key,
                          item.id,
                          tempConsent
                        )}
                        onPurposeToggle={() =>
                          toggleCategory(type.key, item.id, gvl)
                        }
                        onLegitimateInterestToggle={() =>
                          toggleCategoryLegitimateInterest(
                            type.key,
                            item.id,
                            gvl
                          )
                        }
                      />
                    )
                  );
                })}
              </>
            )}
          </div>
        );
      }
    );
  };

  return (
    <>
      {gvl ? (
        <>{purposes(gvl)}</>
      ) : (
        <div class={cx("d-flex", "justify-content-center", "spinnerWrapper")}>
          <Spinner />
        </div>
      )}
    </>
  );
};

export default TCFPurposeSettings;
